import React from "react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import * as Yup from "yup"
import { EMAIL_VALIDATION_REGEX } from "utilities/constants"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import TextField from "components/ReactHookForm/TextField"
import TextArea from "components/ReactHookForm/TextArea"
import AlertBox, { AlertBoxType } from "components/elements/AlertBox/AlertBox"
import Icon from "components/elements/Icon"
import { inviteLeadRetrievalPurchase } from "api/events"
import { leadRetrievalLicensesCacheKey } from "queries/events"
import { successToast } from "utilities/toasts"

const schema = Yup.object().shape({
  email: Yup.string().matches(EMAIL_VALIDATION_REGEX, "Invalid email format").required(),
  message: Yup.string(),
})

type Props = {
  eventId: string
  onHide: () => void
}

const InviteExhibitorToPurchase = ({ eventId, onHide }: Props) => {
  const queryClient = useQueryClient()

  const handleSubmit = async ({ email, message }: { email: string, message: string }) => {
    await inviteLeadRetrievalPurchase(eventId, email, message || undefined)
    await queryClient.invalidateQueries(leadRetrievalLicensesCacheKey(eventId))
    successToast({ title: `Invite sent to ${email}` })
    onHide()
  }

  return (
    <ModalForm
      initialValues={{ email: "", message: "" }}
      validationSchema={schema}
      onSubmit={handleSubmit}
      onClose={onHide}
      title="Invite Exhibitor to Purchase"
      submitText="Send Invite"
      size="lg"
    >
      {({ control }) => (
        <Stack spacing={0}>
          <Text mb={4}>
            Send an email invitation so the exhibitor can create an account and purchase a
            Lead Retrieval license directly from Diobox.
          </Text>
          <TextField
            vertical
            label="Exhibitor Email"
            name="email"
            control={control}
            variant="outline"
            placeholder="Enter email address"
          />
          <TextArea
            vertical
            label={(
              <>
                Message <Text as="span" fontWeight="normal" color="gray.500">(optional)</Text>
              </>
            )}
            name="message"
            control={control}
            placeholder="Add a note to the invitation email"
          />
          <Box mt={3}>
            <AlertBox
              type={AlertBoxType.Upgrade}
              text={(
                <Flex gap={3} alignItems="flex-start">
                  <Icon icon="info" color="blue.500" mt={1} />
                  <Stack spacing={1}>
                    <Text fontWeight="semibold">What happens next?</Text>
                    <Text>
                      The exhibitor will receive a link to create an account and purchase a
                      license for this event. The exhibitor is listed
                      as <Text as="span" fontWeight="bold">Invited</Text> until payment is complete.
                    </Text>
                  </Stack>
                </Flex>
              )}
            />
          </Box>
        </Stack>
      )}
    </ModalForm>
  )
}

export default InviteExhibitorToPurchase
